import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useNavigate, useLocation, useOutlet, Link, UNSAFE_LocationContext as LocationContext } from "react-router-dom";
import { SG_FETCH_USER_EMPLOYEE, CLEAR_PASSWORD_DATA } from "constants/actions";
import { DomainContext } from 'layouts/LayoutContext';
import { DomainList } from "data/domain";
import NavData from "./navigation";
import ErrorWrapper from "error/index";
import { createMedia } from "@artsy/fresnel";
import PropTypes from "prop-types";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "components/ui/breadcrumb";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "components/ui/sidebar";
import {
  LayoutDashboard,
  CalendarCheck,
  BarChart3,
  MessageSquare,
  NotebookTabs,
  ListChecks,
  Building2,
  HelpCircle,
  Mail,
  FileText,
  Folder,
  Calendar,
  CalendarRange,
  Users,
  Boxes,
  ChevronDown,
  Upload,
} from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

const { MediaContextProvider, Media } = createMedia({
  breakpoints: {
    mobile: 0,
    tablet: 768,
    computer: 1024,
  },
});

const ComplianceCheck = (props) => {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  let location = useLocation();
  const [employeeId, setEmployeeId] = useState(false);
  const [employee, setEmployee] = useState(false);

  const { get_auth, get_employees, get_error } = useSelector(
    (state) => ({
      get_auth: state.auth,
      get_employees: state.employees,
      get_error: state.errors,
    }),
    shallowEqual
  );

  // console.log("get_auth", get_auth); 
  // console.log("get_error", get_error);

  const IsStaff = useSelector((state) => state.auth?.user?.is_staff, shallowEqual);

  useEffect(() => {
    const timer1 = setTimeout(() => {
      if (!IsStaff) {
        navigate('/app/switch-org');
      }
    }, 2000);
    return () => clearTimeout(timer1);
  }, [IsStaff, navigate]);

  useEffect(() => {
    if (get_auth?.token) {
      setEmployeeId(get_auth.employee_id);
    }
  }, [get_auth]);

  useEffect(() => {
    if (get_auth?.token) {
      setEmployeeId(get_auth.employee_id);
    }
  }, [get_auth]);

  useEffect(() => {
    if (!employee && Number(employeeId) > 0) {
      dispatch({
        type: SG_FETCH_USER_EMPLOYEE,
        payload: { id: employeeId },
      });
    }
  }, [dispatch, employeeId, employee]);

  useEffect(() => {
    if (get_auth?.token && employeeId) {
      setEmployee(get_employees?.userEmp);
    }
  }, [dispatch, get_employees, employeeId, get_auth]);

  useEffect(() => {
    if (!get_auth?.token) {
      navigate("/account/login", { state: { from: location } });
    }
  }, [get_auth, navigate, location]);

  useEffect(() => {
    if (employee?.email_status === 9 && location.pathname !== "/app/dashboard") {
      navigate("/app/dashboard");
    }
  }, [employee, location, navigate]);

  // Handle 401 Unauthorized error
  useEffect(() => {
    if (get_error?.status === 401) {
      dispatch({ type: CLEAR_PASSWORD_DATA });
      navigate("/account/login", { state: { from: location } });
    }
  }, [get_error, dispatch, navigate, location]);

  return null;
};

const ContentWrapper = ({ children }) => (
  <div className="relative flex w-full justify-center">
    <div
      className="w-full max-w-[1400px] px-4"
      style={{ boxSizing: "border-box" }}
    >
      {children}
    </div>
  </div>
);

const ResponsiveContentContainer = ({ children }) => (
  <div className="w-full overflow-hidden">{children}</div>
);

const MobileContainer = () => {
  const location = useLocation();
  const outlet = useOutlet();
  const hasMountedRef = useRef(false);
  const prefersReducedMotion = useReducedMotion();
  const parentLocationContext = useContext(LocationContext);

  const createState = useCallback(
    (loc, out) => ({
      location: loc,
      outlet: out,
      navigationType: parentLocationContext?.navigationType ?? "POP",
    }),
    [parentLocationContext?.navigationType]
  );

  const pendingStateRef = useRef(createState(location, outlet));
  const [renderState, setRenderState] = useState(() => createState(location, outlet));
  const [transitionStage, setTransitionStage] = useState("idle");

  useEffect(() => {
    hasMountedRef.current = true;
  }, []);

  useEffect(() => {
    const nextState = createState(location, outlet);
    pendingStateRef.current = nextState;

    if (prefersReducedMotion) {
      setRenderState(nextState);
      setTransitionStage("idle");
      return;
    }

    if (location.key !== renderState.location.key && transitionStage !== "exiting") {
      setTransitionStage("exiting");
    }
  }, [location, outlet, prefersReducedMotion, renderState.location.key, transitionStage, createState]);

  const variants = useMemo(() => {
    if (prefersReducedMotion) {
      return {
        idle: { opacity: 1, y: 0 },
        entering: { opacity: 1, y: 0 },
        exiting: { opacity: 1, y: 0 },
        initial: { opacity: 1, y: 0 },
      };
    }

    return {
      idle: { opacity: 1, y: 0 },
      entering: { opacity: 1, y: 0 },
      exiting: { opacity: 0, y: -8 },
      initial: { opacity: 0, y: 8 },
    };
  }, [prefersReducedMotion]);

  const animateVariant = prefersReducedMotion
    ? "idle"
    : transitionStage === "exiting"
      ? "exiting"
      : transitionStage === "entering"
        ? "entering"
        : "idle";

  const initialVariant = hasMountedRef.current ? "initial" : "idle";

  const handleAnimationComplete = useCallback(() => {
    if (prefersReducedMotion) {
      return;
    }

    if (transitionStage === "exiting") {
      setRenderState(pendingStateRef.current);
      setTransitionStage("entering");
      return;
    }

    if (transitionStage === "entering") {
      setTransitionStage("idle");
    }
  }, [prefersReducedMotion, transitionStage]);

  const iconMap = useMemo(() => ({
    Dashboard: LayoutDashboard,
    "Action Planning": CalendarCheck,
    "Marketing ROI": BarChart3,
    "Gpt Web Chat": MessageSquare,
    "Data Story Log / Gpt Recom Log": NotebookTabs,
    "Task Template": ListChecks,
    Organizations: Building2,
    Questions: HelpCircle,
    "Email Templates": Mail,
    "Pdf Templates": FileText,
    "PDF Reports": FileText,
    Categories: Folder,
    "Surveys / Brief Schedule": Calendar,
    "Schedule Reports": CalendarRange,
    Groups: Users,
    "Public Uploads": Upload,
  }), []);

  const DefaultIcon = Boxes;

  const topLevelTitles = useMemo(() => ([
    "Questions",
    "Categories",
    "Surveys / Brief Schedule",
    "Organizations",
    "Public Uploads",
    "PDF Reports",
  ]), []);

  const topLevelItems = useMemo(() => (NavData || [])
    .filter(i => !i.header && i.group === "MainMenu" && !i.subNav && i.link && topLevelTitles.includes(i.title)), [topLevelTitles]);

  const groupedWithSubNav = useMemo(() => (NavData || [])
    .filter(i => !i.header && i.group === "MainMenu" && Array.isArray(i.subNav) && i.subNav.length > 0), []);

  const otherSingles = useMemo(() => (NavData || [])
    .filter(i => !i.header && i.group === "MainMenu" && !i.subNav && i.link && !topLevelTitles.includes(i.title)), [topLevelTitles]);

  const [openGroup, setOpenGroup] = useState({});
  const toggleGroup = (key) => setOpenGroup((prev) => ({ ...prev, [key]: !prev[key] }));

  const isActive = (path) => location.pathname.startsWith(path);

  const flatNav = useMemo(() => {
    const items = [];
    (NavData || []).forEach((n) => {
      if (n.link && n.title) {
        items.push({ link: n.link, title: n.title, parent: null });
      }
      if (Array.isArray(n.subNav)) {
        n.subNav.forEach((s) => {
          items.push({ link: s.link, title: s.title, parent: n.title || null });
        });
      }
    });
    return items;
  }, []);

  const crumbs = useMemo(() => {
    const path = location.pathname;
    const home = { label: "Dashboard", to: "/app/dashboard" };

    let best = null;
    flatNav.forEach((it) => {
      if (it.link && path.startsWith(it.link)) {
        if (!best || it.link.length > best.link.length) {
          best = it;
        }
      }
    });

    const arr = [home];

    if (best) {
      if (best.parent) {
        arr.push({ label: best.parent, to: null });
      }
      if (best.link !== home.to) {
        arr.push({ label: best.title, to: best.link === path ? null : best.link });
      }
      const rest = path.slice(best.link.length);
      if (rest && rest !== "/") {
        const segs = rest.split("/").filter(Boolean);
        segs.forEach((seg) => {
          const label = decodeURIComponent(seg)
            .replace(/[-_]/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
          arr.push({ label, to: null });
        });
      }
    } else {
      const afterApp = path.startsWith("/app") ? path.slice("/app".length) : path;
      const segs = afterApp.split("/").filter(Boolean);
      segs.forEach((seg, idx) => {
        const label = decodeURIComponent(seg)
          .replace(/[-_]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        const prefix = ["/app", ...segs.slice(0, idx + 1)].join("/");
        arr.push({ label, to: idx < segs.length - 1 ? prefix : null });
      });
    }

    return arr;
  }, [location.pathname, flatNav]);

  return (
    <SidebarProvider>
      <Sidebar variant="sidebar" collapsible="offcanvas" className="bg-background">
        <SidebarHeader>
          <div className="flex items-center gap-2 px-2 py-1.5">
            <span className="text-sm font-semibold">Innerlogic Admin</span>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Core</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {topLevelItems.map((item) => {
                  const Icon = iconMap[item.title] || DefaultIcon;
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={isActive(item.link)} tooltip={item.title}>
                        <Link to={item.link}>
                          <Icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {groupedWithSubNav.map((group) => (
            <SidebarGroup key={group.title}>
              <SidebarGroupLabel className="flex items-center justify-between">
                <span>{group.title}</span>
                <button onClick={() => toggleGroup(group.title)} className="rounded-md p-1 hover:bg-sidebar-accent">
                  <ChevronDown className={openGroup[group.title] ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>
              </SidebarGroupLabel>
              {openGroup[group.title] && (
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuSub>
                        {group.subNav.map((sub) => (
                          <SidebarMenuSubItem key={sub.link}>
                            <SidebarMenuSubButton asChild isActive={isActive(sub.link)}>
                              <Link to={sub.link}>
                                <span>{sub.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              )}
            </SidebarGroup>
          ))}

          {otherSingles.length > 0 && (
            <SidebarGroup>
              <SidebarGroupLabel className="flex items-center justify-between">
                <span>More</span>
                <button onClick={() => toggleGroup("__more__")} className="rounded-md p-1 hover:bg-sidebar-accent">
                  <ChevronDown className={openGroup["__more__"] ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>
              </SidebarGroupLabel>
              {openGroup["__more__"] && (
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuSub>
                        {otherSingles.map((item) => {
                          const Icon = iconMap[item.title] || DefaultIcon;
                          return (
                            <SidebarMenuSubItem key={item.link}>
                              <SidebarMenuSubButton asChild isActive={isActive(item.link)}>
                                <Link to={item.link}>
                                  <Icon />
                                  <span>{item.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              )}
            </SidebarGroup>
          )}
        </SidebarContent>
        <SidebarFooter>
          <div className="px-2 py-1 text-xs text-muted-foreground">v1</div>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset>
        <div className="flex h-12 items-center gap-2 border-b px-2">
          <SidebarTrigger />
          <Breadcrumb>
            <BreadcrumbList>
              {crumbs.map((c, i) => {
                const isLast = i === crumbs.length - 1;
                return (
                  <React.Fragment key={`${c.label}-${i}`}>
                    <BreadcrumbItem>
                      {isLast || !c.to ? (
                        isLast ? (
                          <BreadcrumbPage>{c.label}</BreadcrumbPage>
                        ) : (
                          <span className="text-sm text-muted-foreground">{c.label}</span>
                        )
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={c.to}>{c.label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator />}
                  </React.Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <ContentWrapper>
          <ResponsiveContentContainer>
            <LocationContext.Provider
              value={{
                location: renderState.location,
                navigationType: renderState.navigationType,
              }}
            >
              <motion.div
                key={renderState.location.key}
                initial={initialVariant}
                animate={animateVariant}
                variants={variants}
                transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: "easeOut" }}
                style={{ willChange: "opacity, transform" }}
                onAnimationComplete={handleAnimationComplete}
              >
                <ErrorWrapper>
                  {renderState.outlet}
                </ErrorWrapper>
              </motion.div>
            </LocationContext.Provider>
          </ResponsiveContentContainer>
        </ContentWrapper>
      </SidebarInset>
    </SidebarProvider>
  );
};

const ResponsiveContainer = ({ children }) => {
  const domain = window.location.host;
  return (
    <MediaContextProvider>
      <DomainContext.Provider value={DomainList?.[domain] ? DomainList?.[domain] : Object.values(DomainList)[0]}>
        <ComplianceCheck />
        <MobileContainer>{children}</MobileContainer>
      </DomainContext.Provider>
    </MediaContextProvider>
  );
};

ResponsiveContainer.propTypes = {
  children: PropTypes.node,
};

export default ResponsiveContainer;